/**
 * Einstellungen einer Quelle (Spec 5.2/5.3, Phase 3): Rueckkanal, Metadaten-
 * Schema und Anzeige-Template.
 *
 * Zwei Dinge, die das UI hier durchhalten muss:
 *
 *   1. **Geheimnisse gibt es genau einmal.** API-Key und Webhook-Secret
 *      stehen nach dem Erzeugen nirgends mehr — der Kasten sagt das auch,
 *      damit niemand spaeter danach sucht.
 *   2. **JSON wird vor dem Speichern geprueft.** Die beiden Textfelder sind
 *      die einfachste Form, die Spec 3.2 verlangt („JSON-Schema" bzw.
 *      „Anzeige-Template"); ein Formular-Editor waere fuer 3–4 Mandanten
 *      Aufwand ohne Nutzen. Ein Tippfehler darf aber nicht als 422 vom Server
 *      zurueckkommen, sondern wird direkt am Feld gemeldet.
 */
import {
  metadataDisplaySchema,
  metadataSchemaSchema,
  type Source,
  type UpdateSourceInput,
} from '@tickethub/shared';
import { KeyRound, Trash2 } from 'lucide-react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';

export type SourceSettingsProps = {
  source: Source;
  busy: boolean;
  onSave: (input: UpdateSourceInput) => Promise<void>;
  onRotateKey: () => Promise<void>;
  onRotateSecret: () => Promise<void>;
  onDelete: () => Promise<void>;
  onShowDeliveries: () => void;
};

/** `null` → leeres Feld, Objekt → eingerueckt formatiert. */
function toText(value: unknown): string {
  return value === null || value === undefined ? '' : JSON.stringify(value, null, 2);
}

export function SourceSettings({
  source,
  busy,
  onSave,
  onRotateKey,
  onRotateSecret,
  onDelete,
  onShowDeliveries,
}: SourceSettingsProps) {
  const { t } = useTranslation();

  const [webhookUrl, setWebhookUrl] = useState(source.webhook_url ?? '');
  const [schemaText, setSchemaText] = useState(toText(source.metadata_schema));
  const [displayText, setDisplayText] = useState(toText(source.metadata_display));
  const [errors, setErrors] = useState<{ schema?: string; display?: string }>({});

  /**
   * Leeres Feld = `null` (Vorgabe loeschen). Sonst parsen und gegen das
   * Schema aus `@tickethub/shared` pruefen — dieselbe Definition, die der
   * Server benutzt (Invariante 2).
   */
  function parseField(
    text: string,
    schema: typeof metadataSchemaSchema | typeof metadataDisplaySchema,
  ): { ok: true; value: unknown } | { ok: false; message: string } {
    if (text.trim().length === 0) return { ok: true, value: null };
    let parsed: unknown;
    try {
      parsed = JSON.parse(text);
    } catch {
      return { ok: false, message: t('source.json_invalid') };
    }
    const result = schema.safeParse(parsed);
    if (!result.success) {
      const issue = result.error.issues[0];
      return {
        ok: false,
        message: `${issue?.path.join('.') || '(root)'}: ${issue?.message ?? ''}`,
      };
    }
    return { ok: true, value: result.data };
  }

  function submit(event: React.FormEvent) {
    event.preventDefault();

    const schema = parseField(schemaText, metadataSchemaSchema);
    const display = parseField(displayText, metadataDisplaySchema);
    const next: { schema?: string; display?: string } = {};
    if (!schema.ok) next.schema = schema.message;
    if (!display.ok) next.display = display.message;
    setErrors(next);
    if (!schema.ok || !display.ok) return;

    const trimmedUrl = webhookUrl.trim();
    void onSave({
      webhook_url: trimmedUrl.length > 0 ? trimmedUrl : null,
      metadata_schema: schema.value as UpdateSourceInput['metadata_schema'],
      metadata_display: display.value as UpdateSourceInput['metadata_display'],
    });
  }

  const needsSecret = webhookUrl.trim().length > 0 && !source.has_webhook_secret;

  return (
    <form className="space-y-4 border-t pt-4" onSubmit={submit}>
      <div className="flex flex-wrap items-center gap-2">
        <Button type="button" variant="outline" size="sm" disabled={busy} onClick={() => void onRotateKey()}>
          <KeyRound aria-hidden="true" />
          {t('source.rotate_key')}
        </Button>
        <Button
          type="button"
          variant="outline"
          size="sm"
          disabled={busy}
          onClick={() => void onRotateSecret()}
        >
          {source.has_webhook_secret ? t('source.rotate_secret') : t('source.create_secret')}
        </Button>
        <Badge variant={source.has_webhook_secret ? 'secondary' : 'outline'}>
          {source.has_webhook_secret ? t('source.secret_set') : t('source.secret_missing')}
        </Badge>
        <Button type="button" variant="outline" size="sm" onClick={onShowDeliveries}>
          {t('webhook.show_deliveries')}
        </Button>
        <Button
          type="button"
          variant="ghost"
          size="icon"
          className="ml-auto"
          disabled={busy}
          aria-label={t('source.delete', { name: source.name })}
          onClick={() => void onDelete()}
        >
          <Trash2 aria-hidden="true" />
        </Button>
      </div>

      <div className="space-y-2">
        <Label htmlFor={`webhook-url-${source.id}`}>{t('source.webhook_url')}</Label>
        <Input
          id={`webhook-url-${source.id}`}
          value={webhookUrl}
          placeholder="https://app.example.com/webhooks/tickethub"
          onChange={(event) => setWebhookUrl(event.target.value)}
        />
        <p className="text-xs text-muted-foreground">{t('source.webhook_url_hint')}</p>
        {needsSecret ? (
          <Alert variant="destructive">
            <AlertDescription>{t('source.secret_required')}</AlertDescription>
          </Alert>
        ) : null}
      </div>

      <div className="space-y-2">
        <Label htmlFor={`metadata-schema-${source.id}`}>{t('source.metadata_schema')}</Label>
        <textarea
          id={`metadata-schema-${source.id}`}
          className="min-h-32 w-full rounded-md border border-input bg-background p-2 font-mono text-xs"
          value={schemaText}
          aria-invalid={errors.schema !== undefined}
          onChange={(event) => setSchemaText(event.target.value)}
        />
        <p className="text-xs text-muted-foreground">{t('source.metadata_schema_hint')}</p>
        {errors.schema ? <p className="text-xs text-destructive">{errors.schema}</p> : null}
      </div>

      <div className="space-y-2">
        <Label htmlFor={`metadata-display-${source.id}`}>{t('source.metadata_display')}</Label>
        <textarea
          id={`metadata-display-${source.id}`}
          className="min-h-32 w-full rounded-md border border-input bg-background p-2 font-mono text-xs"
          value={displayText}
          aria-invalid={errors.display !== undefined}
          onChange={(event) => setDisplayText(event.target.value)}
        />
        <p className="text-xs text-muted-foreground">{t('source.metadata_display_hint')}</p>
        {errors.display ? <p className="text-xs text-destructive">{errors.display}</p> : null}
      </div>

      <Button type="submit" disabled={busy}>
        {t('common.save')}
      </Button>
    </form>
  );
}

/** Der Kasten, der ein frisch erzeugtes Geheimnis **einmalig** zeigt. */
export function SecretOnce({
  title,
  hint,
  value,
  onClose,
}: {
  title: string;
  hint: string;
  value: string;
  onClose: () => void;
}) {
  const { t } = useTranslation();
  return (
    <Alert>
      <AlertTitle>{title}</AlertTitle>
      <AlertDescription>
        <p className="mb-2">{hint}</p>
        <code className="block break-all rounded bg-muted px-2 py-1 text-xs">{value}</code>
        <Button className="mt-2" size="sm" variant="outline" onClick={onClose}>
          {t('common.close')}
        </Button>
      </AlertDescription>
    </Alert>
  );
}
